<template>
  <NavBar pageName="Portability Requests"></NavBar>
  <v-snackbar
      v-model="snackbarVisible"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
  >
    {{ snackbarMessage }}
    <template v-slot:action>
      <v-btn text color="red" @click="snackbarVisible = false">Close</v-btn>
    </template>
  </v-snackbar>

  <v-container style="margin-top: 185px;">
    <h2 class="text-h4 mb-4">Portabilitate</h2>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>
          <v-icon class="mr-2">mdi-swap-horizontal</v-icon>
          Cereri de Portabilitate
        </span>
        <v-btn color="primary" size="small" @click="loadPortRequests" :loading="loading">
          <v-icon class="mr-1">mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <!-- Filters -->
      <v-card-text class="pb-0">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
                v-model="searchText"
                label="Cauta (nume, email, telefon, MSISDN, CNP, serial)"
                prepend-inner-icon="mdi-magnify"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                @keyup.enter="loadPortRequests"
                @click:clear="searchText = ''; loadPortRequests()"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
                v-model="filterStatusId"
                :items="statusOptions"
                item-value="id"
                item-title="name"
                label="Status"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                @update:model-value="loadPortRequests"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
                v-model="filterOperatorId"
                :items="operatorOptions"
                item-value="id"
                item-title="name"
                label="Operator"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                @update:model-value="loadPortRequests"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
                v-model="filterDateFrom"
                type="date"
                label="De la"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="loadPortRequests"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
                v-model="filterDateTo"
                type="date"
                label="Pana la"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="loadPortRequests"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text>
        <v-data-table-server
            v-model:items-per-page="itemsPerPage"
            :headers="headers"
            :items="portRequests"
            :items-length="totalCount"
            :loading="loading"
            :page="page"
            @update:page="onPageChange"
            @update:items-per-page="onItemsPerPageChange"
            class="elevation-1"
        >
          <template v-slot:[`item.status_name`]="{ item }">
            <v-chip :color="getStatusColor(item.status_name)" size="small">
              {{ item.status_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.destination_msisdn`]="{ item }">
            <span class="font-weight-medium">{{ item.destination_msisdn }}</span>
          </template>

          <template v-slot:[`item.port_operator_name`]="{ item }">
            <v-chip size="small" variant="outlined">
              {{ item.port_operator_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
                icon
                size="small"
                variant="text"
                color="primary"
                @click="openDetails(item)"
            >
              <v-icon>mdi-eye</v-icon>
              <v-tooltip activator="parent" location="top">Detalii</v-tooltip>
            </v-btn>
            <v-btn
                icon
                size="small"
                variant="text"
                color="warning"
                @click="openStatusDialog(item)"
            >
              <v-icon>mdi-pencil</v-icon>
              <v-tooltip activator="parent" location="top">Update Status</v-tooltip>
            </v-btn>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="800px">
      <v-card v-if="selectedRequest">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-2">mdi-swap-horizontal</v-icon>
            Cerere #{{ selectedRequest.id }}
          </span>
          <v-chip :color="getStatusColor(selectedRequest.status_name)" size="small">
            {{ selectedRequest.status_name }}
          </v-chip>
        </v-card-title>

        <v-card-text>
          <!-- Main Info -->
          <v-row>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-3">
                <div class="text-subtitle-2 text-grey mb-2">MSISDN de Portat</div>
                <div class="text-h5 font-weight-bold">{{ selectedRequest.destination_msisdn }}</div>
                <div class="mt-2">
                  <v-chip size="small" variant="tonal" color="primary">
                    {{ selectedRequest.port_operator_name }}
                  </v-chip>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-3">
                <div class="text-subtitle-2 text-grey mb-2">Contact</div>
                <div><strong>{{ selectedRequest.name }}</strong></div>
                <div class="text-body-2">{{ selectedRequest.email }}</div>
                <div class="text-body-2">{{ selectedRequest.contact_phone }}</div>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Personal Data -->
          <div class="text-subtitle-1 font-weight-bold mb-3">Date Personale</div>
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">CNP</div>
              <div>{{ selectedRequest.cnp || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">Serie CI</div>
              <div>{{ selectedRequest.id_serial || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">Numar CI</div>
              <div>{{ selectedRequest.serial_ci || '-' }}</div>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12">
              <div class="text-caption text-grey">Adresa</div>
              <div>{{ formatAddress(selectedRequest) }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Subscription Info -->
          <div class="text-subtitle-1 font-weight-bold mb-3">Informatii Abonament</div>
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">Tip Abonament Donor</div>
              <div>
                <v-chip
                    size="small"
                    :color="selectedRequest.op_donor_subscription_type_id === 1 ? 'success' : 'info'"
                >
                  {{ selectedRequest.op_donor_subscription_type_id === 1 ? 'Prepaid' : 'Postpaid' }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="4" v-if="selectedRequest.op_donor_subscription_type_id === 1">
              <div class="text-caption text-grey">Serial SIM Donor</div>
              <div>{{ selectedRequest.serial_sim_donor || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4" v-if="selectedRequest.op_donor_subscription_type_id === 2">
              <div class="text-caption text-grey">Cod Client Donor</div>
              <div>{{ selectedRequest.donor_client_code || '-' }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Documente Atasate -->
          <div class="text-subtitle-1 font-weight-bold mb-3">Documente Atasate</div>
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey mb-1">Copie Act Identitate</div>
              <div v-if="selectedRequest.id_document_url">
                <v-btn
                  variant="tonal"
                  color="primary"
                  size="small"
                  :href="selectedRequest.id_document_url"
                  target="_blank"
                >
                  <v-icon start>mdi-file-download</v-icon>
                  Copie CI
                </v-btn>
              </div>
              <div v-else>
                <v-file-input
                  v-model="uploadFiles.id_document"
                  label="Incarca Copie CI"
                  accept="image/*,.pdf"
                  variant="outlined"
                  density="compact"
                  prepend-icon="mdi-upload"
                  hide-details
                />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey mb-1">Ultima Factura</div>
              <div v-if="selectedRequest.last_invoice_url">
                <v-btn
                  variant="tonal"
                  color="primary"
                  size="small"
                  :href="selectedRequest.last_invoice_url"
                  target="_blank"
                >
                  <v-icon start>mdi-file-download</v-icon>
                  Factura
                </v-btn>
              </div>
              <div v-else>
                <v-file-input
                  v-model="uploadFiles.last_invoice"
                  label="Incarca Factura"
                  accept="image/*,.pdf"
                  variant="outlined"
                  density="compact"
                  prepend-icon="mdi-upload"
                  hide-details
                />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey mb-1">Imputernicire</div>
              <div v-if="selectedRequest.power_of_attorney_url">
                <v-btn
                  variant="tonal"
                  color="primary"
                  size="small"
                  :href="selectedRequest.power_of_attorney_url"
                  target="_blank"
                >
                  <v-icon start>mdi-file-download</v-icon>
                  Imputernicire
                </v-btn>
              </div>
              <div v-else>
                <v-file-input
                  v-model="uploadFiles.power_of_attorney"
                  label="Incarca Imputernicire"
                  accept="image/*,.pdf"
                  variant="outlined"
                  density="compact"
                  prepend-icon="mdi-upload"
                  hide-details
                />
              </div>
            </v-col>
          </v-row>
          <v-row dense v-if="hasFilesToUpload" class="mt-2">
            <v-col cols="12" class="text-right">
              <v-btn
                color="primary"
                size="small"
                @click="uploadDocuments"
                :loading="uploadingDocs"
              >
                <v-icon start>mdi-cloud-upload</v-icon>
                Incarca Documentele
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- SMS Codes -->
          <div class="text-subtitle-1 font-weight-bold mb-3">Coduri SMS</div>
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">Cod SMS General</div>
              <div class="font-weight-medium">{{ selectedRequest.sms_code || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">Cod SMS Origine</div>
              <div class="font-weight-medium">{{ selectedRequest.sms_code_origin || '-' }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-grey">Cod SMS Destinatie</div>
              <div class="font-weight-medium">{{ selectedRequest.sms_code_destination || '-' }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Dates -->
          <div class="text-subtitle-1 font-weight-bold mb-3">Date</div>
          <v-row dense>
            <v-col cols="12" md="3">
              <div class="text-caption text-grey">Creat</div>
              <div>{{ formatDateTime(selectedRequest.created_at) }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption text-grey">Actualizat</div>
              <div>{{ formatDateTime(selectedRequest.updated_at) }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption text-grey">Data Start</div>
              <div>{{ formatDate(selectedRequest.start_date) || '-' }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption text-grey">Data End</div>
              <div>{{ formatDate(selectedRequest.end_date) || '-' }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Status History -->
          <div class="text-subtitle-1 font-weight-bold mb-3">Istoric Status</div>
          <v-timeline density="compact" side="end" v-if="statusHistory.length > 0">
            <v-timeline-item
                v-for="(status, index) in statusHistory"
                :key="status.id"
                :dot-color="index === 0 ? 'primary' : 'grey'"
                size="small"
            >
              <div class="d-flex justify-space-between align-center">
                <div>
                  <v-chip size="small" :color="getStatusColor(status.status_name)">
                    {{ status.status_name }}
                  </v-chip>
                  <span class="ml-2 text-caption text-grey">
                    de {{ status.user_name || 'System' }}
                  </span>
                </div>
                <span class="text-caption text-grey">
                  {{ formatDateTime(status.created_at) }}
                </span>
              </div>
            </v-timeline-item>
          </v-timeline>
          <div v-else class="text-grey text-body-2">Nu exista istoric</div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" color="red" @click="showDetailsDialog = false">Inchide</v-btn>
          <v-btn color="primary" @click="openStatusDialog(selectedRequest)">
            <v-icon class="mr-1">mdi-pencil</v-icon>
            Update Status
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Status Dialog -->
    <v-dialog v-model="showStatusDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-pencil</v-icon>
          Update Status - Cerere #{{ statusRequest?.id }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <div class="text-caption text-grey">MSISDN</div>
            <div class="text-h6">{{ statusRequest?.destination_msisdn }}</div>
          </div>

          <div class="mb-4">
            <div class="text-caption text-grey">Status Curent</div>
            <v-chip :color="getStatusColor(statusRequest?.status_name)" size="small">
              {{ statusRequest?.status_name }}
            </v-chip>
          </div>

          <v-select
              v-model="newStatusId"
              :items="statusTypes"
              item-value="id"
              item-title="name"
              label="Status Nou"
              variant="outlined"
              :rules="[v => !!v || 'Selecteaza un status']"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showStatusDialog = false">Anuleaza</v-btn>
          <v-btn
              color="primary"
              @click="updateStatus"
              :loading="updatingStatus"
              :disabled="!newStatusId"
          >
            Salveaza
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { log } from "@/composables/useLog";
import {
  getPortRequestsList,
  getPortRequestDetails,
  getPortRequestOptions,
  updatePortRequestStatus,
  uploadPortRequestDocuments
} from "@/composables/usePortRequestsAPI";
import store from "@/store";

export default {
  name: 'PortabilityRequestsListView',
  components: { NavBar },
  data() {
    return {
      token: '',
      isManager: false,
      loading: false,
      portRequests: [],
      totalCount: 0,
      page: 1,
      itemsPerPage: 20,

      // Filters
      searchText: '',
      filterStatusId: null,
      filterOperatorId: null,
      filterDateFrom: null,
      filterDateTo: null,

      // Options
      statusTypes: [],
      operatorOptions: [],
      statusOptions: [],

      // Details dialog
      showDetailsDialog: false,
      selectedRequest: null,
      statusHistory: [],

      // Document upload
      uploadFiles: {
        id_document: null,
        last_invoice: null,
        power_of_attorney: null
      },
      uploadingDocs: false,

      // Status update dialog
      showStatusDialog: false,
      statusRequest: null,
      newStatusId: null,
      updatingStatus: false,

      // Snackbar
      snackbarVisible: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 3000,

      headers: [
        { title: "ID", key: "id", width: "70px" },
        { title: "MSISDN", key: "destination_msisdn", width: "130px" },
        { title: "Nume", key: "name", width: "150px" },
        { title: "Email", key: "email", width: "180px" },
        { title: "Telefon", key: "contact_phone", width: "120px" },
        { title: "Operator Donor", key: "port_operator_name", width: "130px" },
        { title: "Status", key: "status_name", width: "120px" },
        { title: "Data", key: "created_at", width: "100px" },
        { title: "Actiuni", key: "actions", sortable: false, width: "100px" },
      ]
    };
  },
  computed: {
    hasFilesToUpload() {
      const f = this.uploadFiles;
      const hasId = Array.isArray(f.id_document) ? f.id_document.length > 0 : !!f.id_document;
      const hasInv = Array.isArray(f.last_invoice) ? f.last_invoice.length > 0 : !!f.last_invoice;
      const hasPoa = Array.isArray(f.power_of_attorney) ? f.power_of_attorney.length > 0 : !!f.power_of_attorney;
      return hasId || hasInv || hasPoa;
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarVisible = true;
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'warning',
        'in_progress': 'info',
        'processing': 'info',
        'approved': 'success',
        'completed': 'success',
        'rejected': 'error',
        'cancelled': 'error',
        'failed': 'error'
      };
      return colors[status?.toLowerCase()] || 'grey';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('ro-RO');
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatAddress(request) {
      const parts = [request.address, request.city, request.county].filter(p => p);
      return parts.join(', ') || '-';
    },

    async loadOptions() {
      const response = await getPortRequestOptions(this.token);
      if (!response.error) {
        this.statusTypes = response.status_types || [];
        this.operatorOptions = response.port_operators || [];
        this.statusOptions = [{ id: null, name: 'Toate' }, ...this.statusTypes];
      }
    },

    async loadPortRequests() {
      this.loading = true;
      log("Loading port requests");

      const params = {
        page: this.page,
        per_page: this.itemsPerPage
      };

      if (this.searchText) params.search = this.searchText;
      if (this.filterStatusId) params.status_id = this.filterStatusId;
      if (this.filterOperatorId) params.operator_id = this.filterOperatorId;
      if (this.filterDateFrom) params.date_from = this.filterDateFrom;
      if (this.filterDateTo) params.date_to = this.filterDateTo;

      const response = await getPortRequestsList(this.token, params);

      if (!response.error) {
        this.portRequests = response.port_requests || [];
        this.totalCount = response.total_count || 0;
        log("Loaded " + this.portRequests.length + " port requests");
      } else {
        this.showSnackbar(response.message || "Error loading port requests", "error");
      }

      this.loading = false;
    },

    onPageChange(newPage) {
      this.page = newPage;
      this.loadPortRequests();
    },

    onItemsPerPageChange(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.page = 1;
      this.loadPortRequests();
    },

    async openDetails(item) {
      this.selectedRequest = item;
      this.statusHistory = [];
      this.uploadFiles = { id_document: null, last_invoice: null, power_of_attorney: null };
      this.showDetailsDialog = true;

      // Load full details
      const response = await getPortRequestDetails(this.token, item.id);
      if (!response.error) {
        this.selectedRequest = response.port_request;
        this.statusHistory = response.status_history || [];
      }
    },

    openStatusDialog(item) {
      this.statusRequest = item;
      this.newStatusId = item.status_id;
      this.showStatusDialog = true;
    },

    async updateStatus() {
      if (!this.newStatusId || !this.statusRequest) return;

      this.updatingStatus = true;

      const response = await updatePortRequestStatus(
          this.token,
          this.statusRequest.id,
          this.newStatusId
      );

      if (!response.error) {
        this.showSnackbar("Status actualizat cu succes", "success");
        this.showStatusDialog = false;
        this.showDetailsDialog = false;
        this.loadPortRequests();
      } else {
        this.showSnackbar(response.message || "Error updating status", "error");
      }

      this.updatingStatus = false;
    },

    async uploadDocuments() {
      if (!this.selectedRequest) return;

      this.uploadingDocs = true;
      const f = this.uploadFiles;
      const idDoc = Array.isArray(f.id_document) ? f.id_document[0] : f.id_document;
      const lastInv = Array.isArray(f.last_invoice) ? f.last_invoice[0] : f.last_invoice;
      const poa = Array.isArray(f.power_of_attorney) ? f.power_of_attorney[0] : f.power_of_attorney;

      const response = await uploadPortRequestDocuments(
        this.token,
        this.selectedRequest.id,
        {
          id_document: idDoc || null,
          last_invoice: lastInv || null,
          power_of_attorney: poa || null
        }
      );

      if (!response.error) {
        this.showSnackbar("Documentele au fost incarcate cu succes", "success");
        // Reload details to show the new document links
        const detailsResponse = await getPortRequestDetails(this.token, this.selectedRequest.id);
        if (!detailsResponse.error) {
          this.selectedRequest = detailsResponse.port_request;
        }
        this.uploadFiles = { id_document: null, last_invoice: null, power_of_attorney: null };
      } else {
        this.showSnackbar(response.message || "Eroare la incarcarea documentelor", "error");
      }

      this.uploadingDocs = false;
    }
  },
  created() {
    const { getLoggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
      log("PortabilityRequestsListView created");
      this.token = token.value;

      const currentUser = store.getters.getUser;
      const userRoles = currentUser?.roles || [];
      this.isManager = userRoles.some(r =>
          ['root', 'admin', 'portability_manager'].includes(r.name || r)
      );

      // Load data
      Promise.all([
        this.loadOptions(),
        this.loadPortRequests()
      ]);
    });
  }
};
</script>
